
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Leaf, TrendingDown, Target, Zap } from 'lucide-react';

const ImpactPage: React.FC = () => {
  const data = [
    { month: '1월', carbon: 85 },
    { month: '2월', carbon: 92 },
    { month: '3월', carbon: 105 },
    { month: '4월', carbon: 118 },
    { month: '5월', carbon: 132 },
    { month: '6월', carbon: 154 },
  ];

  const currentProjects = [
    { name: 'A 지자체 주민 태양광', status: '운영 중', location: '경기도', capacity: '2.4MW' },
    { name: 'B 기업 RE100 컨설팅', status: '진행 중', location: '서울', capacity: '-' },
    { name: 'C 도시 자전거 마일리지', status: '운영 중', location: '충청남도', capacity: '1.2만명' },
    { name: 'D 마을 에너지 자립', status: '착공 예정', location: '전라북도', capacity: '0.8MW' },
  ];

  return (
    <div className="bg-white">
      <section className="py-24 bg-btLightGray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-black text-gray-900 mb-6 text-center">임팩트 및 성과</h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-16">
            블루테라의 솔루션들이 만들어낸 가시적인 탄소저감 데이터와 프로젝트 현황을 공개합니다.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
             <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-6">
                <div className="p-4 bg-btGreen/10 rounded-xl text-btGreen">
                  <Leaf className="h-8 w-8" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-400">총 탄소 저감량</div>
                  <div className="text-3xl font-black text-gray-900">1,250 <span className="text-lg">Tons</span></div>
                </div>
             </div>
             <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-6">
                <div className="p-4 bg-btBlue/10 rounded-xl text-btBlue">
                  <Zap className="h-8 w-8" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-400">생산 재생에너지</div>
                  <div className="text-3xl font-black text-gray-900">12,400 <span className="text-lg">MWh</span></div>
                </div>
             </div>
             <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-6">
                <div className="p-4 bg-btOrange/10 rounded-xl text-btOrange">
                  <Target className="h-8 w-8" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-400">참여 지자체 및 기업</div>
                  <div className="text-3xl font-black text-gray-900">48 <span className="text-lg">곳</span></div>
                </div>
             </div>
          </div>

          <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                <TrendingDown className="h-6 w-6 text-btGreen" /> 월별 탄소 저감 실적 추이
              </h2>
              <div className="text-xs text-gray-400">(단위: CO2e Tons)</div>
            </div>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip 
                    cursor={{fill: '#f8fafc'}}
                    contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}
                  />
                  <Bar dataKey="carbon" radius={[4, 4, 0, 0]}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === data.length - 1 ? '#f16522' : '#004b8d'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-12">현재 진행 중인 프로젝트</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-gray-100">
                  <th className="py-4 font-bold text-gray-600">프로젝트명</th>
                  <th className="py-4 font-bold text-gray-600">상태</th>
                  <th className="py-4 font-bold text-gray-600">지역</th>
                  <th className="py-4 font-bold text-gray-600">규모/용량</th>
                </tr>
              </thead>
              <tbody>
                {currentProjects.map((p, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-6 font-bold text-gray-800">{p.name}</td>
                    <td className="py-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        p.status === '운영 중' ? 'bg-green-100 text-green-700' : 
                        p.status === '진행 중' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-6 text-gray-600">{p.location}</td>
                    <td className="py-6 text-gray-600">{p.capacity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImpactPage;
